import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Button, Icon, Flex } from "@chakra-ui/react";
import { FaChartLine, FaBullseye, FaLayerGroup, FaArrowRight, FaCheckCircle, FaSolarPanel, FaDollarSign, FaHome } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { GoogleReviews } from "../../../components/chakra/GoogleReviews/GoogleReviews";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";

const PropertyHome: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property"
        fallbackTitle="Property Investment Solutions"
        fallbackDescription="Data-driven investment strategies with solar value-add"
      />
      <NavbarWithCallToAction />

      {/* Hero Section - full-width split layout: text left on white, image bleeding to screen edge */}
      <Box bg="white" overflow="hidden">
        <SimpleGrid columns={{ base: 1, md: 2 }} alignItems="center" minH={{ md: "85vh" }}>
          {/* Left — text, aligned to the container.xl centre line */}
          <Flex justify={{ md: "flex-end" }} px={{ base: 4, md: 8 }} py={{ base: 16, md: 0 }}>
            <VStack
              align="flex-start"
              spacing={7}
              py={{ base: 8, md: 20 }}
              pr={{ md: 12 }}
              maxW={{ md: "640px" }}
              w="full"
            >
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="black"
                color={NAVY}
                lineHeight="1.08"
                letterSpacing="-0.02em"
              >
                One Property Australia
              </Heading>
              <Text fontSize={{ base: "lg", md: "xl" }} color="gray.700" fontWeight="semibold" lineHeight="1.3" maxW="lg">
                Build Retirement Wealth Beyond Superannuation.
              </Text>
              <Text fontSize={{ base: "lg", md: "xl" }} color="gray.600" lineHeight="1.8" maxW="lg">
                If you're earning $200k+ and concerned your super won't deliver the retirement you deserve, property investment offers a proven path to financial security and tax efficiency.
              </Text>
              <HStack spacing={4} pt={4} flexWrap="wrap">
                <Button as={RouterLink} to="/property/consultation" size="lg" bg={PROPERTY_PRIMARY} color="white" _hover={{ bg: "#0a6459" }} rightIcon={<FaArrowRight />} px={8} transition="all 0.2s">
                  Start Your Journey
                </Button>
                <Button as={RouterLink} to="/property/strategy/data-driven" size="lg" variant="outline" borderColor={PROPERTY_PRIMARY} color={NAVY} _hover={{ bg: "#0D7F6E14", borderColor: "#0a6459" }} px={8} transition="all 0.2s">
                  View Strategies
                </Button>
              </HStack>
            </VStack>
          </Flex>

          {/* Right — hero image, full bleed to screen edge */}
          <Box
            position="relative"
            h={{ base: "360px", md: "100%" }}
            minH={{ md: "85vh" }}
            overflow="hidden"
          >
            <Box
              as="img"
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1920&q=80"
              alt="Property investment advisers reviewing a portfolio strategy"
              position="absolute"
              top={0}
              right={0}
              h="full"
              w="full"
              objectFit="cover"
              objectPosition="center"
            />
            {/* Soft gradient blending into white on the left */}
            <Box
              position="absolute"
              top={0} left={0} bottom={0}
              w="30%"
              bgGradient="linear(to-r, white, transparent)"
              display={{ base: "none", md: "block" }}
            />
          </Box>
        </SimpleGrid>
      </Box>

      {/* Stats Section - navy background */}
      <Box
        position="relative"
        color="white"
        py={20}
        bg={NAVY}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8}>
            <Heading size="xl" textAlign="center" textShadow="0 2px 4px rgba(0,0,0,0.3)">
              Helping Australians Build Retirement Wealth
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} w="full" pt={8}>
              <VStack spacing={2}>
                <Heading size="2xl" color="white" textShadow="0 2px 8px rgba(0,0,0,0.4)">$100M+</Heading>
                <Text fontSize="md" color="rgba(255, 255, 255, 0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)">Portfolio Value Managed</Text>
              </VStack>
              <VStack spacing={2}>
                <Heading size="2xl" color="white" textShadow="0 2px 8px rgba(0,0,0,0.4)">18.5%</Heading>
                <Text fontSize="md" color="rgba(255, 255, 255, 0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)">Avg. Annual Return</Text>
              </VStack>
              <VStack spacing={2}>
                <Heading size="2xl" color="white" textShadow="0 2px 8px rgba(0,0,0,0.4)">150+</Heading>
                <Text fontSize="md" color="rgba(255, 255, 255, 0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)">Properties Advised</Text>
              </VStack>
              <VStack spacing={2}>
                <Heading size="2xl" color="white" textShadow="0 2px 8px rgba(0,0,0,0.4)">95%</Heading>
                <Text fontSize="md" color="rgba(255, 255, 255, 0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)">Client Satisfaction</Text>
              </VStack>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Three Strategy Pillars - matching Home page styling */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={8}>
          <Heading size="xl" textAlign="center" color="gray.800">
            Our Three Investment Pillars
          </Heading>
          <Text fontSize="lg" textAlign="center" color="gray.600" maxW="2xl">
            A structured approach to building wealth outside super—designed for busy professionals who want clarity, not complexity
          </Text>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full" pt={8}>
            {/* Pillar 1: Data-Driven */}
            <Box
              borderRadius="xl"
              overflow="hidden"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ transform: "translateY(-4px)", shadow: "2xl", borderColor: PROPERTY_PRIMARY }}
              transition="all 0.3s"
              bg="white"
            >
              <Box h="220px" overflow="hidden">
                <Box as="img" src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" alt="Data-driven property analysis" w="full" h="full" objectFit="cover" />
              </Box>
              <Box p={8} textAlign="left">
                <Icon as={FaChartLine} boxSize={8} color={PROPERTY_PRIMARY} mb={3} />
                <Heading size="md" color={NAVY} mb={3}>Data-Driven Analysis</Heading>
                <Text color="gray.600" fontSize="sm" mb={4}>
                  Remove the guesswork from property investment with research-backed suburb selection and growth projections you can trust
                </Text>
                <VStack spacing={2} align="start" mb={5}>
                  {["Market trend analysis", "Suburb comparison tools", "Investment risk assessment"].map((f) => (
                    <HStack key={f}>
                      <Icon as={FaCheckCircle} color={PROPERTY_PRIMARY} boxSize={4} />
                      <Text fontSize="sm" color="gray.700">{f}</Text>
                    </HStack>
                  ))}
                </VStack>
                <Button as={RouterLink} to="/property/strategy/data-driven" bg={PROPERTY_PRIMARY} color="white" size="sm" _hover={{ bg: "#0a6459" }} transition="all 0.2s">
                  Learn More →
                </Button>
              </Box>
            </Box>

            {/* Pillar 2: Goal-Based Planning */}
            <Box
              borderRadius="xl"
              overflow="hidden"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ transform: "translateY(-4px)", shadow: "2xl", borderColor: PROPERTY_PRIMARY }}
              transition="all 0.3s"
              bg="white"
            >
              <Box h="220px" overflow="hidden">
                <Box as="img" src="https://images.unsplash.com/photo-1434626881859-194d67b2b86f?w=800&q=80" alt="Retirement-focused property planning" w="full" h="full" objectFit="cover" />
              </Box>
              <Box p={8} textAlign="left">
                <Icon as={FaBullseye} boxSize={8} color={PROPERTY_PRIMARY} mb={3} />
                <Heading size="md" color={NAVY} mb={3}>Retirement-Focused Planning</Heading>
                <Text color="gray.600" fontSize="sm" mb={4}>
                  Build a portfolio that complements your superannuation—creating the passive income and financial security you need for retirement
                </Text>
                <VStack spacing={2} align="start" mb={5}>
                  {["Personalized strategies", "Milestone tracking", "Timeline optimization"].map((f) => (
                    <HStack key={f}>
                      <Icon as={FaCheckCircle} color={PROPERTY_PRIMARY} boxSize={4} />
                      <Text fontSize="sm" color="gray.700">{f}</Text>
                    </HStack>
                  ))}
                </VStack>
                <Button as={RouterLink} to="/property/strategy/goals" bg={PROPERTY_PRIMARY} color="white" size="sm" _hover={{ bg: "#0a6459" }} transition="all 0.2s">
                  Learn More →
                </Button>
              </Box>
            </Box>

            {/* Pillar 3: Diversification */}
            <Box
              borderRadius="xl"
              overflow="hidden"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ transform: "translateY(-4px)", shadow: "2xl", borderColor: PROPERTY_PRIMARY }}
              transition="all 0.3s"
              bg="white"
            >
              <Box h="220px" overflow="hidden">
                <Box as="img" src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80" alt="Property portfolio diversification" w="full" h="full" objectFit="cover" />
              </Box>
              <Box p={8} textAlign="left">
                <Icon as={FaLayerGroup} boxSize={8} color={PROPERTY_PRIMARY} mb={3} />
                <Heading size="md" color={NAVY} mb={3}>Portfolio Diversification</Heading>
                <Text color="gray.600" fontSize="sm" mb={4}>
                  Reduce your reliance on super and market volatility with a balanced property portfolio built for long-term wealth
                </Text>
                <VStack spacing={2} align="start" mb={5}>
                  {["Risk mitigation", "Asset class mixing", "Geographic spread"].map((f) => (
                    <HStack key={f}>
                      <Icon as={FaCheckCircle} color={PROPERTY_PRIMARY} boxSize={4} />
                      <Text fontSize="sm" color="gray.700">{f}</Text>
                    </HStack>
                  ))}
                </VStack>
                <Button as={RouterLink} to="/property/strategy/diversification" bg={PROPERTY_PRIMARY} color="white" size="sm" _hover={{ bg: "#0a6459" }} transition="all 0.2s">
                  Learn More →
                </Button>
              </Box>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Key Benefits Section - matching Home page gray.50 styling */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Heading size="xl" textAlign="center" color="gray.800">
              Why High-Income Earners Choose Us
            </Heading>
            <Text fontSize="lg" textAlign="center" color="gray.600" maxW="2xl">
              Clear, practical strategies for professionals who want long-term wealth building—not quick wins or unnecessary complexity
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} pt={8}>
              <Box
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                overflow="hidden"
                _hover={{ transform: "translateY(-4px)", shadow: "xl", borderColor: PROPERTY_PRIMARY }}
                transition="all 0.3s"
              >
                <Box h="200px" overflow="hidden">
                  <Box as="img" src="https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=600&q=80" alt="Solar panels adding value to investment property" w="full" h="full" objectFit="cover" />
                </Box>
                <Box p={6}>
                  <HStack spacing={3} mb={3}>
                    <Icon as={FaSolarPanel} color={PROPERTY_PRIMARY} boxSize={5} />
                    <Heading size="sm" color="gray.800">Solar Value-Add</Heading>
                  </HStack>
                  <Text color="gray.600" fontSize="sm">
                    Boost rental yields and property value while claiming valuable tax deductions through strategic solar integration
                  </Text>
                </Box>
              </Box>

              <Box
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                overflow="hidden"
                _hover={{ transform: "translateY(-4px)", shadow: "xl", borderColor: PROPERTY_PRIMARY }}
                transition="all 0.3s"
              >
                <Box h="200px" overflow="hidden">
                  <Box as="img" src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=600&q=80" alt="Strategic tax planning and minimisation" w="full" h="full" objectFit="cover" />
                </Box>
                <Box p={6}>
                  <HStack spacing={3} mb={3}>
                    <Icon as={FaDollarSign} color={PROPERTY_PRIMARY} boxSize={5} />
                    <Heading size="sm" color="gray.800">Tax Minimisation</Heading>
                  </HStack>
                  <Text color="gray.600" fontSize="sm">
                    Earning $200k+ means paying significant tax—our strategies help you legally reduce your tax bill while building wealth
                  </Text>
                </Box>
              </Box>

              <Box
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                overflow="hidden"
                _hover={{ transform: "translateY(-4px)", shadow: "xl", borderColor: PROPERTY_PRIMARY }}
                transition="all 0.3s"
              >
                <Box h="200px" overflow="hidden">
                  <Box as="img" src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=600&q=80" alt="Professional property investment guidance" w="full" h="full" objectFit="cover" />
                </Box>
                <Box p={6}>
                  <HStack spacing={3} mb={3}>
                    <Icon as={FaHome} color={PROPERTY_PRIMARY} boxSize={5} />
                    <Heading size="sm" color="gray.800">Structured Guidance</Heading>
                  </HStack>
                  <Text color="gray.600" fontSize="sm">
                    Get a clear long-term plan with ongoing accountability—so you can be confident you're making the right moves now
                  </Text>
                </Box>
              </Box>

              <Box
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                overflow="hidden"
                _hover={{ transform: "translateY(-4px)", shadow: "xl", borderColor: PROPERTY_PRIMARY }}
                transition="all 0.3s"
              >
                <Box h="200px" overflow="hidden">
                  <Box as="img" src="https://images.unsplash.com/photo-1434626881859-194d67b2b86f?auto=format&fit=crop&w=600&q=80" alt="Aspirational retirement lifestyle planning" w="full" h="full" objectFit="cover" />
                </Box>
                <Box p={6}>
                  <HStack spacing={3} mb={3}>
                    <Icon as={FaChartLine} color={PROPERTY_PRIMARY} boxSize={5} />
                    <Heading size="sm" color="gray.800">Retirement Roadmap</Heading>
                  </HStack>
                  <Text color="gray.600" fontSize="sm">
                    Track how your property portfolio is working alongside super to deliver your desired retirement lifestyle
                  </Text>
                </Box>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* The One Property Approach */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Heading size="xl" color={NAVY}>We build portfolios. Not just sales.</Heading>
            <Text color="gray.600" fontSize="lg">
              One Property Australia works exclusively with high-income earners who want structured, long-term wealth beyond superannuation. We lead with your numbers, not our inventory.
            </Text>
            <Button as={RouterLink} to="/property/consultation" bg={PROPERTY_PRIMARY} color="white" size="lg" rightIcon={<FaArrowRight />} _hover={{ bg: "#0a6459" }} transition="all 0.2s">
              Book Consultation
            </Button>
          </VStack>
          <Flex direction="column" gap={6}>
            <Box borderRadius="xl" overflow="hidden" h="240px" position="relative" shadow="lg">
              <Box as="img" src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=800&q=80" alt="Aspirational Australian family home with greenery" w="full" h="full" objectFit="cover" />
              <Box position="absolute" bottom={0} left={0} right={0} p={4} bgGradient={`linear(to-t, ${NAVY}CC, transparent)`}>
                <Text fontSize="xs" fontWeight="600" color="white" letterSpacing="wider" textTransform="uppercase">Family lifestyle. Long-term wealth.</Text>
              </Box>
            </Box>
            <SimpleGrid columns={2} spacing={4}>
              {[
                { label: "Assessment First", desc: "We analyse your income, tax position and timeline before recommending anything." },
                { label: "Solar Integration", desc: "Every property strategy includes solar value-add modelling." },
                { label: "Finance Aligned", desc: "Your borrowing structure is designed in-house — no referrals, no gaps." },
                { label: "Prosperity Engine", desc: "Property, solar and finance working as a single integrated system." },
              ].map((item) => (
                <Box key={item.label} p={5} bg="white" borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.100">
                  <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.label}</Text>
                  <Text fontSize="sm" color="gray.600">{item.desc}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Flex>
        </SimpleGrid>
      </Container>

      {/* CTA Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">
              Concerned Super Alone Won't Be Enough?
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl">
              If you're earning well but worried about retirement, let's discuss how property investment can complement your super and reduce your tax burden
            </Text>
            <HStack spacing={4} pt={4}>
              <Button as={RouterLink} to="/property/consultation" bg="white" color={NAVY} _hover={{ bg: "#0a6459", color: "white" }} size="lg" transition="all 0.2s">
                Book Consultation
              </Button>
              <Button as={RouterLink} to="/property/tools/investment-calculator" variant="outline" borderColor="white" color="white" _hover={{ bg: "#0a6459", borderColor: "#0a6459" }} size="lg" transition="all 0.2s">
                View Calculators
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <GoogleReviews placeId={process.env.REACT_APP_GOOGLE_PLACE_ID_PROPERTY || ""} />

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyHome;
